import type {
  CalendarStatus,
  Disposition,
  LeadCall,
  PoolInfo,
  SellerAvailability,
  VoiceToken,
} from './protocol';

// =====================================================================
// Cockpit state machine — the pure reducer behind the SDR calling UI.
//
// Framework-free and side-effect-free (like src/bff.ts), so the whole flow is
// unit-tested without React or a socket (state.test.ts). useCockpit.ts wires it
// to a WebSocket: it dispatches server frames + user actions in, and sends the
// matching client frames out. Transitions are TOLERANT — an action that doesn't
// fit the current phase is ignored (returns the same state) so a duplicate ACK,
// a late frame, or a reconnect can never wedge the UI.
//
// Phases:
//   signed_out  -> not logged in; no WS, no Twilio.
//   connecting  -> signed in; WS opening (or reconnecting).
//   pool_select -> WS open; picking a pool.
//   joining     -> join_pool sent; awaiting pool_joined ACK (UI blocked).
//   waiting     -> in the pool; awaiting a routed call ("calling in pool").
//   on_call     -> a lead was assigned; live call.
//   ending      -> end_call sent; awaiting call_ended ACK (UI blocked).
//
// Leaving a pool does NOT have its own phase: it's tracked by `leaveStatus` (the
// leave is server-driven so a live call is never cut off — see that field), and
// the phase only flips to `pool_select` on the `pool_left` ACK.
// =====================================================================

export type CockpitPhase =
  | 'signed_out'
  | 'connecting'
  | 'pool_select'
  | 'joining'
  | 'waiting'
  | 'on_call'
  | 'ending';

export interface CockpitState {
  phase: CockpitPhase;
  pools: PoolInfo[];
  // The server holds the SDR's seat across a socket loss (suspend-on-disconnect
  // enabled BFF-side). Advertised on every pools frame; false for an older or
  // flag-off server, which keeps today's leave-first refresh advice.
  seatHeld: boolean;
  activePoolId: string | null; // the pool currently joined (waiting/on_call/ending)
  pendingPoolId: string | null; // the pool being joined (joining)
  currentCall: LeadCall | null; // the live lead (on_call/ending)
  // The current lead's client talk track (call dialogue), arriving in a separate
  // `talk_track` frame AFTER lead_assigned. Null until it lands (panel shows the
  // fallback link meanwhile) and cleared whenever the call changes/ends. Carries
  // `callId` so a late frame for a since-ended call is ignored.
  talkTrack: { callId: string; name: string; markdown: string } | null;
  // The owning seller's calendar. Null until it arrives, and null forever when
  // nothing sends one — so a reader must settle on null rather than wait on it.
  // Cleared with the call, like the talk track.
  leadCalendar: {
    callId: string;
    status: CalendarStatus;
    calendar: SellerAvailability | null;
  } | null;
  pendingDisposition: Disposition | null; // the disposition being confirmed (ending)
  // Where the SDR is in an "exit pool" request (the leave is SERVER-driven so a
  // pending call is never cut off):
  //   'none'      -> not leaving.
  //   'requested' -> leave_pool sent; awaiting the server's verdict (button locks).
  //   'draining'  -> the core kept calls in flight; the "please wait for pending
  //                  calls" popup shows. The phase is left UNCHANGED throughout so
  //                  the live call's audio survives; `pool_left` finally exits.
  leaveStatus: 'none' | 'requested' | 'draining';
  leavePendingCalls: number; // how many calls the core is draining (for the popup)
  // Epoch ms of the EXIT CLICK (not the core's reply), so a leave whose reply never
  // arrives is still measurable. Drives the overlay's "this is taking too long" copy.
  leavingSince: number | null;
  // The core paused dialing for a graceful deploy drain (advisory). No new calls
  // start while the node finishes its in-flight calls and is replaced; the SDR
  // keeps its pool and any live call. Drives a banner; cleared on reconnect (the
  // SDR lands on the fresh node) or on leaving the pool.
  deploying: boolean;
  // The standing "still there?" prompt, or null. `expiresAt` is an ISO instant so the
  // countdown never depends on how long the frame took to arrive.
  presenceWarning: { trigger: 'idle' | 'cap'; expiresAt: string } | null;
  // Why the SDR is back on the picker when they did not ask to be. Cleared on the
  // next join, so it reads as an explanation of this arrival rather than a standing
  // condition.
  exitReason: 'idle' | 'cap' | 'silence_gate' | null;
  voiceToken: VoiceToken | null; // BFF-pushed Twilio Voice token (null => simulated)
  error: string | null;
}

export const initialState: CockpitState = {
  phase: 'signed_out',
  pools: [],
  seatHeld: false,
  activePoolId: null,
  pendingPoolId: null,
  currentCall: null,
  talkTrack: null,
  leadCalendar: null,
  pendingDisposition: null,
  leaveStatus: 'none',
  leavePendingCalls: 0,
  leavingSince: null,
  deploying: false,
  presenceWarning: null,
  exitReason: null,
  voiceToken: null,
  error: null,
};

export type CockpitAction =
  | { type: 'presence_warning'; trigger: 'idle' | 'cap'; expiresAt: string }
  | { type: 'presence_clear' }
  | { type: 'sign_in' }
  | { type: 'sign_out' }
  | { type: 'ws_open' }
  | { type: 'ws_closed' }
  | { type: 'pools'; pools: PoolInfo[]; seatHeld?: boolean } // server: available pools (+seatHeld capability)
  | { type: 'voice_token'; voice: VoiceToken } // server: BFF-minted Voice token
  | { type: 'select_pool'; poolId: string } // user: clicked a pool
  | { type: 'pool_joined'; poolId: string } // server: join ACK
  | { type: 'lead_assigned'; call: LeadCall } // server: routed call
  | { type: 'lead_confirmed'; callId: string } // server: a guessed card is now a real human verdict
  | { type: 'talk_track'; callId: string; name: string; markdown: string } // server: client talk track
  | {
      type: 'lead_calendar'; // server: the owning seller's open time
      callId: string;
      status: CalendarStatus;
      calendar: SellerAvailability | null;
    }
  | { type: 'end_call' } // user: finished the conversation and clicked End call
  | { type: 'mark_voicemail' } // user: flagged a voicemail AMD missed (no human)
  | { type: 'call_ended'; callId: string } // server: end ACK
  | { type: 'request_leave'; at: number } // user: clicked leave pool (`at` = click time)
  | { type: 'pool_leaving'; pendingCalls: number; at: number } // server: draining pending calls
  | { type: 'abandon_leave' } // user: gave up on a stalled drain → back to the picker
  // server: leave ACK (drain done / nothing in flight). `reason` names an exit imposed on
  // a seat the SDR held; `refusedReason` a join the core never granted — it becomes the
  // picker's error.
  | {
      type: 'pool_left';
      poolId: string;
      reason?: 'idle' | 'cap' | 'silence_gate';
      refusedReason?: string;
    }
  | { type: 'deploying' } // server: dialing paused for a graceful deploy drain
  | { type: 'deploying_timeout' } // client: the advisory aged out with no positive clear
  | { type: 'session_restored'; poolId: string; currentCall: LeadCall | null } // reconnect: restore pool + call
  | { type: 'restore_failed' }; // reconnect: the new task has no session → picker

// The per-session slice — everything tied to being seated in a pool with a live
// call — reset as a unit whenever the SDR drops out of one. Transitions spread it
// and override only what they keep. Typed as the complement of the fields that
// survive a drop, so a new state field can't be silently forgotten here.
// seatHeld survives a drop: it describes the SERVER's capability, not this
// session — the next pools frame refreshes it either way.
const CLEARED_SESSION: Omit<CockpitState, 'phase' | 'pools' | 'voiceToken' | 'error' | 'seatHeld'> =
  {
    activePoolId: null,
    pendingPoolId: null,
    currentCall: null,
    talkTrack: null,
    leadCalendar: null,
    pendingDisposition: null,
    leaveStatus: 'none',
    leavePendingCalls: 0,
    leavingSince: null,
    deploying: false,
    presenceWarning: null,
    exitReason: null,
  };

/**
 * SDR-facing copy for a join the core refused.
 *
 * Reached when the pool changed under the click: the list is polled, so a card can read
 * joinable for a second after the pool stopped being so. Unknown reasons get generic copy
 * rather than the raw token — the core is free to add reasons this build has not seen.
 */
export function refusalMessage(reason: string): string {
  switch (reason) {
    case 'off_hours':
      return 'That pool is outside its leads’ calling hours — pick another for now.';
    case 'closed':
      return 'That pool finished its pass while you were joining.';
    case 'manual_call_active':
      return 'The manual dialer is open in another tab — close it to join a pool.';
    default:
      return 'That pool stopped accepting joins while you were joining it.';
  }
}

export function cockpitReducer(state: CockpitState, action: CockpitAction): CockpitState {
  switch (action.type) {
    case 'sign_in':
      // Sign-in (re)starts from a clean slate, awaiting the socket.
      return { ...initialState, phase: 'connecting' };
    case 'sign_out':
      return initialState;
    case 'ws_open':
      // Socket up: leave the connecting screen for the pool picker. Clear any
      // stale connection error. (A late ws_open in another phase just clears it.)
      if (state.phase === 'connecting') return { ...state, phase: 'pool_select', error: null };
      return { ...state, error: null };
    case 'ws_closed':
      if (state.phase === 'signed_out') return state;
      // Socket lost: the server session is gone, so drop transient session state
      // and show the reconnecting screen. Keep the (stale) pool list to render
      // immediately on reconnect; the BFF re-pushes it anyway.
      return { ...state, ...CLEARED_SESSION, phase: 'connecting' };
    case 'pools':
      // Every pools frame re-states the capability (parse normalizes absence
      // to false), so this never latches a stale true across a reconnect onto
      // a flag-off or rolled-back server.
      return { ...state, pools: action.pools, seatHeld: action.seatHeld ?? false };
    case 'voice_token':
      return { ...state, voiceToken: action.voice };
    case 'select_pool':
      if (state.phase !== 'pool_select') return state;
      return { ...state, phase: 'joining', pendingPoolId: action.poolId, error: null };
    case 'pool_joined':
      if (state.phase !== 'joining') return state;
      return {
        ...state,
        phase: 'waiting',
        activePoolId: action.poolId,
        pendingPoolId: null,
        exitReason: null,
      };
    case 'lead_assigned':
      if (state.phase !== 'waiting') return state;
      // New call: clear any prior talk track; the BFF sends a fresh one (if any).
      // A card also drops the deploy banner: dialing has reached this SDR, so telling them
      // to sit tight is at best noise over a live call. Weaker evidence than it looks — a
      // drain leaves already-ringing legs alone, so this card may be one of those rather
      // than proof the fleet is done. Only the BFF knows that, and today it never says.
      return {
        ...state,
        phase: 'on_call',
        currentCall: action.call,
        talkTrack: null,
        leadCalendar: null,
        deploying: false,
      };
    case 'lead_confirmed':
      // Only the card on screen: the SDR can have several legs bridged at once, so
      // this may name one we're not showing. Upgrades in place, leaving the talk
      // track intact.
      if (state.currentCall?.callId !== action.callId) return state;
      return { ...state, currentCall: { ...state.currentCall, confirmed: true } };
    case 'talk_track':
      // Apply only to the live call — drop a track that arrived for a since-ended
      // or replaced call (the BFF guards this too, but a late frame can still race).
      if (state.currentCall?.callId !== action.callId) return state;
      return {
        ...state,
        talkTrack: { callId: action.callId, name: action.name, markdown: action.markdown },
      };
    case 'lead_calendar':
      // Same stale guard as the talk track: a lookup can outlive the call it was
      // made for.
      if (state.currentCall?.callId !== action.callId) return state;
      return {
        ...state,
        leadCalendar: {
          callId: action.callId,
          status: action.status,
          calendar: action.calendar,
        },
      };
    case 'end_call':
      // Carries NO disposition (Decision 3): the SDR asserts the event, never a
      // label — kept structurally identical to mark_voicemail below.
      if (state.phase !== 'on_call') return state;
      return { ...state, phase: 'ending', pendingDisposition: null };
    case 'mark_voicemail':
      // The SDR judged this a voicemail AMD missed. Like end_call it leaves
      // `on_call` — which drives the Twilio hangup via the phase effect in
      // cockpit.tsx — and blocks the UI on the call_ended round-trip, but carries
      // NO disposition: a voicemail is "no human reached", not a conversation
      // outcome. The BFF replies call_ended(null), returning the SDR to waiting.
      if (state.phase !== 'on_call') return state;
      return { ...state, phase: 'ending', pendingDisposition: null };
    case 'call_ended':
      // Clears the card from `on_call` too, not just `ending`: a real call can end
      // before the SDR dispositions it (the contact hangs up / the core ends it →
      // a null-disposition call_ended). Either way we go back to waiting. This holds
      // during a leave too — the phase follows the call; `leaveStatus` (the popup)
      // is untouched and only clears on `pool_left`.
      if (state.phase !== 'on_call' && state.phase !== 'ending') return state;
      // Only the card on screen (as lead_confirmed above). The browser holds exactly one
      // Twilio call, so acting on a sibling's end drops the phase and cockpit.tsx's phase
      // effect hangs up the live human.
      if (state.currentCall?.callId !== action.callId) return state;
      return {
        ...state,
        phase: 'waiting',
        currentCall: null,
        talkTrack: null,
        leadCalendar: null,
        pendingDisposition: null,
      };
    case 'request_leave':
      // The SDR asked to exit. We do NOT change the phase (changing it would drive
      // the Twilio hangup and cut off any live call) — the LEAVE IS SERVER-DRIVEN:
      // useCockpit sends leave_pool and the core decides. If the SDR has calls in
      // flight the core drains them and replies `pool_leaving` (→ popup); otherwise
      // it replies `pool_left`. Only meaningful while in a pool and not already going.
      if (state.leaveStatus !== 'none') return state;
      if (state.phase !== 'waiting' && state.phase !== 'on_call' && state.phase !== 'ending')
        return state;
      return { ...state, leaveStatus: 'requested', leavingSince: action.at };
    case 'pool_leaving':
      // The core kept pending calls running and is draining them. Show the "please
      // wait for pending calls" popup; the phase stays as-is so the live call's
      // audio survives until it ends (then `pool_left` arrives).
      if (state.leaveStatus === 'none') return state;
      return {
        ...state,
        leaveStatus: 'draining',
        leavePendingCalls: action.pendingCalls,
        // The core re-broadcasts as each leg drains; keep the first stamp or a stalled
        // leave would look freshly started on every one.
        leavingSince: state.leavingSince ?? action.at,
      };
    case 'presence_warning': {
      // Only somebody holding a seat can be asked to confirm they still want it.
      if (state.activePoolId == null) return state;
      const standing = state.presenceWarning;
      if (standing?.trigger === action.trigger && standing.expiresAt === action.expiresAt) {
        return state;
      }
      return {
        ...state,
        presenceWarning: { trigger: action.trigger, expiresAt: action.expiresAt },
      };
    }
    case 'presence_clear':
      return state.presenceWarning == null ? state : { ...state, presenceWarning: null };
    case 'pool_left':
      // Left the pool — either nothing was in flight, or the drain finished. Back to
      // the pool picker, clearing any leave/call state. Ignore an ACK for a pool we're
      // no longer in: a late one must not evict the SDR from a pool they since joined.
      if (state.activePoolId == null || action.poolId !== state.activePoolId) return state;
      // Refuse an unrequested exit while a card is up, for the same reason
      // `abandon_leave` does: clearing `currentCall` drives the softphone teardown, and
      // that would hang up on a live human. The server drains instead of sending this,
      // so arriving here with a call means something upstream is wrong — and the safe
      // reading of that is to keep talking.
      if (action.reason != null && state.currentCall != null) return state;
      return {
        ...state,
        ...CLEARED_SESSION,
        phase: 'pool_select',
        exitReason: action.reason ?? null,
        // A refusal never had a seat to explain, so it speaks through the picker's error
        // line instead. Every other route back to the picker leaves `error` alone.
        ...(action.refusedReason != null ? { error: refusalMessage(action.refusedReason) } : {}),
      };
    case 'abandon_leave':
      // The SDR gave up on a drain that never finished. Land exactly where `pool_left`
      // lands — the picker — because that's where re-joining is possible, and joining
      // any pool is what makes the core release them.
      //
      // Refuse while a card is up: `currentCall` drives the Twilio effect, so clearing
      // it here would hang up a live human. The overlay hides itself when a card is
      // showing, but that's a RENDER-time check — a card can land between paint and click.
      if (state.leaveStatus === 'none' || state.currentCall != null) return state;
      return { ...state, ...CLEARED_SESSION, phase: 'pool_select' };
    case 'deploying':
      // Advisory: the core paused dialing for a deploy drain. Show the banner; do
      // NOT touch phase/pool/call — any live call keeps running and the SDR stays
      // in the pool. Cleared on reconnect (ws_closed) or on leaving (pool_left).
      return { ...state, deploying: true };
    case 'deploying_timeout':
      // The advisory has no retraction signal: nothing positive arrives when the
      // deploy finishes, so an idle SDR on a quiet pool whose socket survived the
      // roll (a core-only deploy) would wear the banner until the next card. The
      // client ages it out instead — a fresh deploy re-asserts it.
      return state.deploying ? { ...state, deploying: false } : state;
    case 'session_restored':
      // Reconnected to a task that still has our session (a BFF deploy swap): restore
      // the pool + live call so the swap is invisible, instead of bouncing to the
      // picker. When the SAME call is restored the phase stays `on_call`, so the Twilio
      // effect (keyed on phase, cockpit.tsx) never re-fires and the audio is untouched.
      // Clears the deploy banner — we've landed on the fresh task.
      return {
        ...state,
        ...CLEARED_SESSION,
        phase: action.currentCall ? 'on_call' : 'waiting',
        activePoolId: action.poolId,
        currentCall: action.currentCall,
        error: null,
      };
    case 'restore_failed':
      // Reconnected, but the new task has no session for us (it genuinely ended, or the
      // restore timed out) — fall back to the picker, clearing any stale call/pool.
      return { ...state, ...CLEARED_SESSION, phase: 'pool_select' };
  }
}

// Convenience selector for the UI: the display name of a pool by id.
export function poolName(state: CockpitState, poolId: string | null): string {
  if (!poolId) return '';
  return state.pools.find((p) => p.id === poolId)?.name ?? poolId;
}
